import React, { useEffect, useState } from "react";
import { Bishop } from "../../interfaces/bishop";
import { School } from "../../interfaces/school";
import { getBishop, getBishopsWithParams } from "../../services/bishopService";
import { getSchool, getSchoolsWithParams } from "../../services/schoolService";

interface FillDetailPopupProps {
  type: string;
  onClose: (selectedItem?: Bishop | School) => void;
}

const FillDetailPopup: React.FC<FillDetailPopupProps> = ({ type, onClose }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [bishops, setBishops] = useState<Bishop[]>([]);
  const [schools, setSchools] = useState<School[]>([]);
  const [selectedOptionID, setSelectedOptionID] = useState<number | null>(null);

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      const trimmedSearchTerm = searchTerm.trim();
      setDebouncedSearchTerm(trimmedSearchTerm);
    }, 500);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    const loadData = async () => {
      if (!debouncedSearchTerm) {
        setBishops([]);
        setSchools([]);
        return;
      }

      try {
        if (type === "bishop") {
          const { bishops } = await getBishopsWithParams(
            1,
            10,
            debouncedSearchTerm,
          );
          setBishops(bishops);
        } else if (type === "school") {
          const { schools } = await getSchoolsWithParams(
            1,
            10,
            debouncedSearchTerm,
          );
          setSchools(schools);
        } else {
          console.log("type not expected");
        }
      } catch (error) {
        console.log("error loading data");
      }
    };

    loadData();
  }, [debouncedSearchTerm, type]);

  const handleSelection = (id: number) => {
    setSelectedOptionID(id);
  };

  const handleSaveSelection = async () => {
    if (!selectedOptionID) return;

    try {
      let selectedItem: Bishop | School | undefined;

      if (type === "bishop") {
        selectedItem = await getBishop(selectedOptionID);
      } else {
        selectedItem = await getSchool(selectedOptionID);
      }

      if (selectedItem) {
        onClose(selectedItem);
      }
    } catch (error) {
      console.error("Error fetching selected item:", error);
    }
  };

  return (
    <>
      <div className="background-mask" onClick={() => onClose()}></div>
      <div className="filter-popup-xxl">
        <div className="close-button-container">
          <button className="close-button" onClick={() => onClose()}>
            <i className="bi bi-x-circle"></i>
          </button>
        </div>
        <div className="filter-header">
          <h2>Select a {type === "bishop" ? "Bishop" : "School"}</h2>
          <div>Use the search below.</div>
        </div>
        <br />
        <div className="dialog-container">
          <div className="search-container" style={{ width: "100%" }}>
            <input
              style={{ border: "none", width: "100%", outline: "none" }}
              type="text"
              placeholder={`Search ${type} by name...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm.trim() ? (
              <i
                className="bi bi-x-circle clear-icon"
                onClick={() => setSearchTerm("")}
                style={{ cursor: "pointer" }}
              ></i>
            ) : (
              <i className="bi bi-search search-icon"></i>
            )}
          </div>

          <br />

          <div className="table-container-fill">
            <table className="styled-table">
              <thead>
                {type === "bishop" && debouncedSearchTerm !== "" && (
                  <tr>
                    <th style={{ width: "250px" }}>Bishop</th>
                    <th style={{ width: "200px" }}>Denomination</th>
                    <th style={{ width: "250px" }}>Merge Field</th>
                    <th style={{ width: "250px" }}>Contact</th>
                  </tr>
                )}
                {type === "school" && debouncedSearchTerm !== "" && (
                  <tr>
                    <th style={{ width: "250px" }}>School</th>
                    <th style={{ width: "150px" }}>State</th>
                    <th style={{ width: "200px" }}>City</th>
                    <th style={{ width: "250px" }}>Contact</th>
                  </tr>
                )}
              </thead>
              <tbody>
                {bishops.length !== 0
                  ? bishops.map((item) => (
                      <tr
                        key={item.bishopID}
                        className={
                          selectedOptionID === item.bishopID ? "selected" : ""
                        }
                        onClick={() => handleSelection(item.bishopID)}
                      >
                        <td>{item.bishopName}</td>
                        <td>{item.denomination}</td>
                        <td>{item.diocese}</td>
                        <td>{item.contact}</td>
                      </tr>
                    ))
                  : schools.map((item) => (
                      <tr
                        key={item.schoolID}
                        className={
                          selectedOptionID === item.schoolID ? "selected" : ""
                        }
                        onClick={() => handleSelection(item.schoolID)}
                      >
                        <td>{item.name}</td>
                        <td>{item.state}</td>
                        <td>{item.city}</td>
                        <td>{item.contact}</td>
                      </tr>
                    ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="filter-actions">
          <button
            className="smbtn btn-green"
            onClick={handleSaveSelection}
            disabled={!selectedOptionID}
          >
            Save Selection
          </button>
        </div>
      </div>
    </>
  );
};

export default FillDetailPopup;
